/* eslint-disable max-lines */
import { OnboardingStatusIndicator } from '@strata/react-status-indicators';

export const SHIPPED_ACTIVITY = 'Pharmacy scheduled delivery to member';

export const DocumentableFillActivitiesMap = {
    'Member preference at alternative SP': 'Member preference at alternative pharmacy',
    'Waiting for Rx': 'Pharmacy waiting for Rx',
    'Waiting on callback from prescriber': 'Pharmacy waiting on callback from prescriber',
    'Prescriber outreach attempts exhausted': 'Pharmacy exhausted all attempts to obtain Rx',
    'Referred to manufacturer hub': 'Pharmacy has been referred to manufacturer hub',
    'Rx in hand': 'Pharmacy has Rx in hand',
    'Rx in hand - additional information needed from MDO':
        'Pharmacy needs Rx clarification from prescriber',
    'Confirming patient eligibility': 'Pharmacy confirming patient eligibility',
    'Payer verification needed': 'Pharmacy verifying information with payer',
    'PA issue - auth needed': 'Prior Authorization needed',
    'PA issue - non-formulary': 'Drug is non-formulary',
    'PA issue - prior auth in process': 'Prior Authorization submitted',
    'PA issue - prior auth appeal pending': 'Prior Authorization is pending appeal',
    'PA issue - prior auth appeal denied': 'Prior Authorization appeal has been denied',
    'PA issue - resolved': 'Pharmacy Prior Authorization issue is resolved',
    'Adjudication issue - refill too soon': 'Rx refill too soon',
    'Adjudication issue - plan limitation exceeded': 'Plan limitation exceeded',
    'Adjudication issue - max day supply': 'Claim exceeds max day supply',
    'Adjudication issue - drug utilization review': 'Drug utilization review',
    'Adjudication issue - lock issue': 'Pharmacy assignment has lock issue',
    'Adjudication issue - resolved': 'Pharmacy adjudication issue is resolved',
    'Claims issue - rebilling': 'Claim has rebilling issue',
    'Claims issue - rebilling resolved': 'Claim rebilling issue resolved',
    'Waiting for financial assistance information':
        'Pharmacy waiting for financial assistance info',
    'Financial assistance information obtained': 'Pharmacy obtained financial assistance info',
    'Waiting on member call back': 'Pharmacy is waiting for patient to call back',
    'Member outreach attempts exhausted': 'Pharmacy exhausted all attempts to reach patient',
    'Waiting on nursing services': 'Pharmacy waiting on nursing services',
    'Ready for shipment': 'Pharmacy is coordinating shipment details',
    [SHIPPED_ACTIVITY]: 'Pharmacy scheduled delivery to patient',
    'Pharmacy filled Rx - claim not received': 'Pharmacy filled Rx - claim not received',
    'Reported future NBD - refill too soon': 'Pharmacy placed Rx on hold - refill too soon',
    'Reported future NBD - member has drug on hand':
        'Pharmacy placed Rx on hold - patient has drug on hand',
    'Therapy delay unspecified': 'Pharmacy placed Rx on hold - delay unspecfied',
    'Rx on hold with SP - member hospitalized': 'Pharmacy placed Rx on hold - patient hospitalized',
    'Rx on hold with SP - MD request': 'Pharmacy placed Rx on hold - prescriber request',
    'Rx on hold with SP - patient request': 'Pharmacy placed Rx on hold - patient request',
    'Rx on hold with SP - other': 'Pharmacy placed Rx on hold - other',
    'Rx on hold - member needs appointment': 'Pharmacy placed Rx on hold - patient needs appt',
    'Rx on hold - clinical issue': 'Pharmacy placed Rx on hold - clinical issue',
    'Cancelled - member coverage termed': 'Pharmacy cancelled Rx - patient eligibility termed',
    'Cancelled - buy and bill': 'Pharmacy cancelled Rx - prescriber buy & bill',
    'Cancelled - clinical drug study': 'Pharmacy cancelled Rx - clinical drug study',
    'Cancelled - clinical issue': 'Pharmacy cancelled Rx - clinical issue',
    'Cancelled - death': 'Pharmacy cancelled Rx - patient death',
    'Cancelled - denied reauthorization': 'Pharmacy cancelled Rx - reauth denied',
    'Cancelled - discontinued therapy': 'Pharmacy cancelled Rx - therapy discontinued',
    'Cancelled - hospice': 'Pharmacy cancelled Rx - patient in hospice',
    'Cancelled - hospitalized': 'Pharmacy cancelled Rx - patient hospitalized',
    'Cancelled - insurance/financial assistance':
        'Pharmacy cancelled Rx - Ins/financial assistance',
    'Cancelled - patient request': 'Pharmacy cancelled Rx - patient request',
    'Cancelled - MD request': 'Pharmacy cancelled Rx - prescriber request',
    'Cancelled - OON': 'Pharmacy cancelled Rx - pharmacy out of network',
    'Cancelled - pharmacy unable to service member state':
        'Pharmacy cancelled Rx - pharmacy unable to service state',
    'Cancelled - other': 'Pharmacy cancelled Rx - other',
    'Cancelled - therapy complete': 'Pharmacy cancelled Rx - therapy complete',
    'Cancelled - therapy change': 'Pharmacy cancelled Rx - therapy change',
    'Cancelled - noncompliance': 'Pharmacy cancelled Rx - patient is noncompliant',
    'Cancelled - nonresponse to therapy': 'Pharmacy cancelled Rx - nonresponsive to therapy',
    'Cancelled - unable to tolerate': 'Pharmacy cancelled Rx - unable to tolerate therapy',
    'Out of stock': 'Drug is out of stock at pharmacy',
    'Drug shortage': 'Manufacturer has drug shortage',
} as const;
export const DocumentableFillActivitiesMapReverse = Object.fromEntries(
    Object.entries(DocumentableFillActivitiesMap).map(([key, value]) => [value, key]),
);
const QuickFilterToFillActivity = {
    [OnboardingStatusIndicator.StatusMessages.RX_NEEDED]: [
        DocumentableFillActivitiesMap['Prescriber outreach attempts exhausted'],
        DocumentableFillActivitiesMap['Referred to manufacturer hub'],
        DocumentableFillActivitiesMap['Waiting for Rx'],
        DocumentableFillActivitiesMap['Waiting on callback from prescriber'],
    ],
    [OnboardingStatusIndicator.StatusMessages.RX_AT_PHARM]: [
        DocumentableFillActivitiesMap['Confirming patient eligibility'],
        DocumentableFillActivitiesMap['Payer verification needed'],
        DocumentableFillActivitiesMap['Rx in hand - additional information needed from MDO'],
        DocumentableFillActivitiesMap['Rx in hand'],
    ],
    [OnboardingStatusIndicator.StatusMessages.PA_ISSUE]: [
        DocumentableFillActivitiesMap['PA issue - auth needed'],
        DocumentableFillActivitiesMap['PA issue - non-formulary'],
        DocumentableFillActivitiesMap['PA issue - prior auth appeal denied'],
        DocumentableFillActivitiesMap['PA issue - prior auth appeal pending'],
        DocumentableFillActivitiesMap['PA issue - prior auth in process'],
        DocumentableFillActivitiesMap['PA issue - resolved'],
    ],
    [OnboardingStatusIndicator.StatusMessages.ADJ_ISSUE]: [
        DocumentableFillActivitiesMap['Adjudication issue - drug utilization review'],
        DocumentableFillActivitiesMap['Adjudication issue - lock issue'],
        DocumentableFillActivitiesMap['Adjudication issue - max day supply'],
        DocumentableFillActivitiesMap['Adjudication issue - plan limitation exceeded'],
        DocumentableFillActivitiesMap['Adjudication issue - refill too soon'],
        DocumentableFillActivitiesMap['Adjudication issue - resolved'],
    ],
    [OnboardingStatusIndicator.StatusMessages.MEMBER_OUTREACH]: [
        DocumentableFillActivitiesMap['Financial assistance information obtained'],
        DocumentableFillActivitiesMap['Member outreach attempts exhausted'],
        DocumentableFillActivitiesMap['Waiting for financial assistance information'],
        DocumentableFillActivitiesMap['Waiting on member call back'],
    ],
    [OnboardingStatusIndicator.StatusMessages.PEND_SHIPMENT]: [
        DocumentableFillActivitiesMap['Pharmacy filled Rx - claim not received'],
        DocumentableFillActivitiesMap[SHIPPED_ACTIVITY],
        DocumentableFillActivitiesMap['Ready for shipment'],
        DocumentableFillActivitiesMap['Waiting on nursing services'],
    ],
    [OnboardingStatusIndicator.StatusMessages.ON_HOLD]: [
        DocumentableFillActivitiesMap['Reported future NBD - member has drug on hand'],
        DocumentableFillActivitiesMap['Reported future NBD - refill too soon'],
        DocumentableFillActivitiesMap['Rx on hold - clinical issue'],
        DocumentableFillActivitiesMap['Rx on hold - member needs appointment'],
        DocumentableFillActivitiesMap['Rx on hold with SP - MD request'],
        DocumentableFillActivitiesMap['Rx on hold with SP - member hospitalized'],
        DocumentableFillActivitiesMap['Rx on hold with SP - other'],
        DocumentableFillActivitiesMap['Rx on hold with SP - patient request'],
        DocumentableFillActivitiesMap['Therapy delay unspecified'],
    ],
    [OnboardingStatusIndicator.StatusMessages.PEND_CANCELED]: [
        DocumentableFillActivitiesMap['Cancelled - buy and bill'],
        DocumentableFillActivitiesMap['Cancelled - clinical drug study'],
        DocumentableFillActivitiesMap['Cancelled - clinical issue'],
        DocumentableFillActivitiesMap['Cancelled - death'],
        DocumentableFillActivitiesMap['Cancelled - denied reauthorization'],
        DocumentableFillActivitiesMap['Cancelled - discontinued therapy'],
        DocumentableFillActivitiesMap['Cancelled - hospice'],
        DocumentableFillActivitiesMap['Cancelled - hospitalized'],
        DocumentableFillActivitiesMap['Cancelled - insurance/financial assistance'],
        DocumentableFillActivitiesMap['Cancelled - MD request'],
        DocumentableFillActivitiesMap['Cancelled - member coverage termed'],
        DocumentableFillActivitiesMap['Cancelled - noncompliance'],
        DocumentableFillActivitiesMap['Cancelled - nonresponse to therapy'],
        DocumentableFillActivitiesMap['Cancelled - OON'],
        DocumentableFillActivitiesMap['Cancelled - other'],
        DocumentableFillActivitiesMap['Cancelled - patient request'],
        DocumentableFillActivitiesMap['Cancelled - pharmacy unable to service member state'],
        DocumentableFillActivitiesMap['Cancelled - therapy change'],
        DocumentableFillActivitiesMap['Cancelled - therapy complete'],
        DocumentableFillActivitiesMap['Cancelled - unable to tolerate'],
        DocumentableFillActivitiesMap['Drug shortage'],
        DocumentableFillActivitiesMap['Member preference at alternative SP'],
        DocumentableFillActivitiesMap['Out of stock'],
    ],
};
const DocumentableFillActivitiesToQuickFilter = Object.fromEntries(
    Object.entries(QuickFilterToFillActivity).flatMap(([key, values]) =>
        values.map((value) => [value, key]),
    ),
);

export const FillActivityDescriptions = {
    [DocumentableFillActivitiesMap['Member preference at alternative SP']]:
        'Pharmacy has identifed that the member has a pharmacy preference that is not the current assigned pharmacy',
    [DocumentableFillActivitiesMap['Waiting for Rx']]:
        'Pharmacy is waiting to receive the prescription',
    [DocumentableFillActivitiesMap['Waiting on callback from prescriber']]:
        'Pharmacy has called the MDO and is awaiting callback',
    [DocumentableFillActivitiesMap['Prescriber outreach attempts exhausted']]:
        'Pharmacy has exhuasted all attempts to reach the prescriber to obtain the prescription',
    [DocumentableFillActivitiesMap['Referred to manufacturer hub']]:
        'Pharmacy has been referred to the manufacturer hub to obtain prescription',
    [DocumentableFillActivitiesMap['Rx in hand']]: 'Assigned pharmacy has the prescription',
    [DocumentableFillActivitiesMap['Rx in hand - additional information needed from MDO']]:
        'Assigned pharmacy has the prescription, but needs additional information from the MDO',
    [DocumentableFillActivitiesMap['Confirming patient eligibility']]:
        'Pharmacy is confirming patient eligibility',
    [DocumentableFillActivitiesMap['Payer verification needed']]:
        'Pharmacy verifying information with the payer',
    [DocumentableFillActivitiesMap['PA issue - auth needed']]:
        'Prior Authorization needed for referral',
    [DocumentableFillActivitiesMap['PA issue - non-formulary']]: 'Drug is non-formulary',
    [DocumentableFillActivitiesMap['PA issue - prior auth in process']]:
        'Prior Authorization has been submitted and is in process',
    [DocumentableFillActivitiesMap['PA issue - prior auth appeal pending']]:
        'Prior Authorization is pending appeal',
    [DocumentableFillActivitiesMap['PA issue - prior auth appeal denied']]:
        'Prior Authorization appeal has been denied',
    [DocumentableFillActivitiesMap['PA issue - resolved']]:
        'Pharmacy Prior Authorization issue is resolved',
    [DocumentableFillActivitiesMap['Adjudication issue - refill too soon']]:
        'Pharmacy has received a claim rejection that the medication is refill too soon',
    [DocumentableFillActivitiesMap['Adjudication issue - plan limitation exceeded']]:
        'Pharmacy has received a claim rejection related to plan limitations exceeded',
    [DocumentableFillActivitiesMap['Adjudication issue - max day supply']]:
        'Pharmacy has received a claim rejection related to max day supply',
    [DocumentableFillActivitiesMap['Adjudication issue - drug utilization review']]:
        'Pharmacy has received a claim rejection that a drug utilization review is needed',
    [DocumentableFillActivitiesMap['Adjudication issue - lock issue']]:
        'Pharmacy has received a claim rejection related to a lock issue',
    [DocumentableFillActivitiesMap['Adjudication issue - resolved']]:
        'Pharmacy claim rejection issue is resolved',
    [DocumentableFillActivitiesMap['Claims issue - rebilling']]:
        'Pharmacy has an issue related to rebilling a claim for this referral',
    [DocumentableFillActivitiesMap['Claims issue - rebilling resolved']]:
        'Pharmacy issue related to rebilling a claim has been resolved',
    [DocumentableFillActivitiesMap['Waiting for financial assistance information']]:
        'Pharmacy is waiting to receive financial information',
    [DocumentableFillActivitiesMap['Waiting on member call back']]:
        'Pharmacy has outreached to member and is waiting on the member to call back',
    [DocumentableFillActivitiesMap['Member outreach attempts exhausted']]:
        'Pharmacy has made all attempts to outreach to the member',
    [DocumentableFillActivitiesMap['Waiting on nursing services']]: 'Waiting on nursing services',
    [DocumentableFillActivitiesMap['Ready for shipment']]:
        'Pharmacy is ready to ship medication and needs to schedule date for delivery to member',
    [DocumentableFillActivitiesMap[SHIPPED_ACTIVITY]]:
        'Pharmacy has set a date for medication delivery to member or has confirmed shipping',
    [DocumentableFillActivitiesMap['Pharmacy filled Rx - claim not received']]:
        'Pharmacy informed that rx has been filled, but claim has not been received',
    [DocumentableFillActivitiesMap['Reported future NBD - refill too soon']]:
        'Pharmacy has received a claim rejection that the medication is refill too soon',
    [DocumentableFillActivitiesMap['Reported future NBD - member has drug on hand']]:
        'Pharmacy has informed that member has drug on hand',
    [DocumentableFillActivitiesMap['Therapy delay unspecified']]:
        'Pharmacy has informed that the members therapy has been delayed',
    [DocumentableFillActivitiesMap['Rx on hold with SP - member hospitalized']]:
        'Pharmacy has informed that member is currently hospitalized and referral is on hold',
    [DocumentableFillActivitiesMap['Rx on hold with SP - MD request']]:
        'Pharmacy has informed that the MDO has requested to place the referral on hold',
    [DocumentableFillActivitiesMap['Rx on hold with SP - patient request']]:
        'Pharmacy has informed that the patient has requested to place the referral on hold',
    [DocumentableFillActivitiesMap['Rx on hold with SP - other']]:
        'Pharmacy has informed us that the referral is on hold',
    [DocumentableFillActivitiesMap['Rx on hold - member needs appointment']]:
        'Pharmacy has informed us that the referral is on hold until the member has appointment with the MDO',
    [DocumentableFillActivitiesMap['Rx on hold - clinical issue']]:
        'Pharmacy has informed that the referral is on hold due to a clinical issue',
    [DocumentableFillActivitiesMap['Cancelled - member coverage termed']]:
        'Members coverage has termed',
    [DocumentableFillActivitiesMap['Cancelled - buy and bill']]:
        'This MDO will buy and bill this medication and is out of scope for FMH',
    [DocumentableFillActivitiesMap['Cancelled - clinical drug study']]:
        'Pharmacy has cancelled referral due to member receiving medication through a clinical drug study',
    [DocumentableFillActivitiesMap['Cancelled - clinical issue']]:
        'Pharmacy has cancelled referral due to member experiencing clinical issue',
    [DocumentableFillActivitiesMap['Cancelled - death']]:
        'Pharmacy has canceled referral due to member being deceased',
    [DocumentableFillActivitiesMap['Cancelled - denied reauthorization']]:
        'Pharmacy has cancelled referral due to reauthorization being denied',
    [DocumentableFillActivitiesMap['Cancelled - discontinued therapy']]:
        'Pharmacy has cancelled referral due to member discontinuation of therapy',
    [DocumentableFillActivitiesMap['Cancelled - hospice']]:
        'Pharmacy has cancelled referral due to member being in hospice',
    [DocumentableFillActivitiesMap['Cancelled - hospitalized']]:
        'Pharmacy has cancelled referral due to member being hospitalized and no longer needing medication',
    [DocumentableFillActivitiesMap['Cancelled - insurance/financial assistance']]:
        'Pharmacy has cancelled referral due to member financial issue or insurance issues',
    [DocumentableFillActivitiesMap['Cancelled - patient request']]:
        'Pharmacy has cancelled referral due to patient request',
    [DocumentableFillActivitiesMap['Cancelled - MD request']]:
        'Pharmacy has cancelled referral due to MDO request',
    [DocumentableFillActivitiesMap['Cancelled - OON']]:
        'Pharmacy has cancelled referral due to the assigned pharmacy being out of network for this referral',
    [DocumentableFillActivitiesMap['Cancelled - pharmacy unable to service member state']]:
        'Pharmacy has cancelled referral due to inability to service the state that the member resides in',
    [DocumentableFillActivitiesMap['Cancelled - other']]: 'Pharmacy has cancelled referral',
    [DocumentableFillActivitiesMap['Cancelled - therapy complete']]:
        'Pharmacy has cancelled referral due to the member completing therapy',
    [DocumentableFillActivitiesMap['Cancelled - therapy change']]:
        'Pharmacy has cancelled referral due to the member having a therapy change',
    [DocumentableFillActivitiesMap['Cancelled - noncompliance']]:
        'Pharmacy has cancelled referral due to the member being noncompliant',
    [DocumentableFillActivitiesMap['Cancelled - nonresponse to therapy']]:
        'Pharmacy has cancelled referral due to the member being nonresponsive to therapy',
    [DocumentableFillActivitiesMap['Cancelled - unable to tolerate']]:
        'Pharmacy has cancelled referral due to member being unable to tolerate the therapy',
    [DocumentableFillActivitiesMap['Out of stock']]:
        'The pharmacy is currently out of stock of this medication',
    [DocumentableFillActivitiesMap['Drug shortage']]:
        'There is a current manufacturer drug shortage of this medication',
} as const;

export const DocumentableFillActivityOptions = Object.keys(
    DocumentableFillActivitiesMapReverse,
).map((key) => ({
    name: key,
    quickFilterKey: DocumentableFillActivitiesToQuickFilter[key],
    secondaryData: FillActivityDescriptions[key as keyof typeof FillActivityDescriptions],
}));

const ViewOnlyFillActivitiesMap = {
    'Referral in process': 'Pharmacy reported referral in process',
    'Escalated to client - prescriber information needed':
        'FMH escalated to plan - prescriber information needed',
    'Provided SP with pharmacy transfer information':
        'FMH requested pharmacy to obtain Rx transfer',
    'URGENT - FMH escalated to FMH Pharmacy - Rx needed':
        'URGENT - FMH escalated to FMH Pharmacy - Rx needed',
    'Escalated to NDP - SP unable to get Rx': 'FMH escalated to FMH pharmacy - Rx needed',
    'NDP initiated Rx transfer from SP attempt 1': 'FMH Pharmacy initiated Rx transfer attempt 1',
    'NDP initiated Rx transfer from SP attempt 2': 'FMH Pharmacy initiated Rx transfer attempt 2',
    'NDP outreach to MDO attempt 1': 'FMH Pharmacy outreach to prescriber attempt 1',
    'NDP outreach to MDO attempt 2': 'FMH Pharmacy outreach to prescriber attempt 2',
    'NDP outreach to MDO attempt 3': 'FMH Pharmacy outreach to prescriber attempt 3',
    'NDP - Rx transferred to SP': 'FMH Pharmacy transferred Rx to pharmacy',
    'NDP - exhausted all attempts to get Rx': 'FMH Pharmacy exhausted all attempts to obtain Rx',
    'Escalated to client - unable to obtain Rx': 'FMH escalated to plan - unable to obtain Rx',
} as const;

export const AllFillActivitiesMap = {
    ...DocumentableFillActivitiesMap,
    ...ViewOnlyFillActivitiesMap,
} as Record<string, string>;

export const VisibleFillActivityOptionsSet = new Set(Object.keys(AllFillActivitiesMap));
